import { BottomNavBar } from "@phaser/ui/navs/BottomNavBar";
import { ShopScrollView } from "@phaser/ui/shop/ShopScrollView";
import { ModalType, ModalManager } from "@phaser/ui/modals/ModalManager";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { CoinShowPanel } from "@phaser/ui/shop/CoinShowPanel";
import { coinManager } from "@/phaser/core/game/CoinManager";
import { shopPurchaseService } from "@phaser/core/shop/ShopPurchaseService";
import { getShopItemById } from "@phaser/core/shop/shopCatalog";
import { shopInventoryManager } from "@phaser/core/shop/ShopInventoryManager";
import { shopEquipManager } from "@phaser/core/shop/ShopEquipManager";
import { ToastManager, ToastType } from "@phaser/ui/toast/ToastManager";
import { t } from "@/i18n/i18n";

export class ShopScene extends Phaser.Scene {
  private modalManager!: ModalManager;
  private toastManager!: ToastManager;
  private backSub?: PluginListenerHandle;
  private isPurchaseConfirmOpen = false;

  private coinPanel!: CoinShowPanel;
  private scrollView?: ShopScrollView;

  constructor() {
    super("ShopScene");
  }

  // preload() {
  // }

  async create(data?: { savedScrollY?: number }) {
    const { width, height } = this.scale;

    this.modalManager = new ModalManager(this);
    this.toastManager = new ToastManager(this);

    // 배경
    const bg = this.add.image(width / 2, height / 2, "shop-bg");
    bg.setDisplaySize(width, height);

    this.add
      .text(width / 2, height / 2 - 550, "shop", {
        fontFamily: "Luckiest Guy",
        fontSize: "120px",
        fontStyle: "normal",
        color: "#FFCC00",
        stroke: "#090040",
        strokeThickness: 20,
      })
      .setOrigin(0.5);

    // 코인 패널
    this.coinPanel = new CoinShowPanel(this, width * 0.8, height * 0.18);
    this.refreshCoinUI();

    // 몬스터 리스트 스크롤 뷰
    this.scrollView = new ShopScrollView(this, {
      x: 0,
      y: height * 0.21,
      width,
      height: height - 420,
      initialScrollY: data?.savedScrollY ?? 0,
    });

    // 하단 네비게이션
    new BottomNavBar(this, width / 2, height, "SHOP");

    // 아이템 구매 확인 열기 이벤트 리스너
    this.events.on("purchase-confirm", this.handlePurchaseConfirm, this);
    this.events.on("purchase-execute", this.handlePurchaseExecute, this);
    this.events.on(
      "purchase-confirm-closed",
      this.handlePurchaseConfirmClosed,
      this,
    );
    this.events.on("equip-item", this.handleEquipItem, this);

    // 네이티브 앱에서 뒤로가기 버튼 처리 (몬스터 카드 모달이 열려있을 때 닫기)
    if (Capacitor.isNativePlatform()) {
      this.registerBackButton();

      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.backSub?.remove();
        this.backSub = undefined;
      });
    }

    // 씬 종료 시 이벤트 정리
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off("purchase-confirm", this.handlePurchaseConfirm, this);
      this.events.off("purchase-execute", this.handlePurchaseExecute, this);
      this.events.off(
        "purchase-confirm-closed",
        this.handlePurchaseConfirmClosed,
        this,
      );
      this.events.off("equip-item", this.handleEquipItem, this);
      this.modalManager.close();
      this.isPurchaseConfirmOpen = false;
    });
  }

  private refreshCoinUI() {
    this.coinPanel.setCoins(coinManager.getBalance());
  }

  private refreshShopUI() {
    this.refreshCoinUI();
    this.scrollView?.refresh();
  }

  private handlePurchaseConfirm(payload: { itemId: string }) {
    this.isPurchaseConfirmOpen = true;

    this.modalManager.open(ModalType.PURCHASE, {
      itemId: payload.itemId,
    });
  }

  private async handlePurchaseExecute(payload: { itemId: string }) {
    // 예 버튼 누르자마자 모달 먼저 닫기
    this.modalManager.close();
    this.isPurchaseConfirmOpen = false;

    const result = await shopPurchaseService.purchase(payload.itemId);

    if (!result.success) {
      let message = t("shop.purchase_failed");

      switch (result.reason) {
        case "ITEM_NOT_FOUND":
          message = t("shop.purchase_failed_item_not_found");
          break;
        case "ALREADY_OWNED":
          message = t("shop.purchase_failed_already_owned");
          break;
        case "NOT_ENOUGH_COINS":
          message = t("shop.purchase_failed_not_enough_coins");
          break;
      }

      this.toastManager.open(ToastType.ALERT, {
        message,
      });

      return;
    }

    // this.refreshCoinUI();
    this.refreshShopUI();

    this.toastManager.open(ToastType.ALERT, {
      message: t("shop.purchase_success"),
    });
  }

  private async handleEquipItem(payload: { itemId: string }) {
    const item = getShopItemById(payload.itemId);
    if (!item) return;

    if (!shopInventoryManager.isOwned(item.id)) {
      return;
    }

    await shopEquipManager.equip(item.category, item.id);
    this.refreshShopUI();
  }

  // 몬스터 카드 모달이 닫힐 때 상태 업데이트
  private handlePurchaseConfirmClosed() {
    this.isPurchaseConfirmOpen = false;
  }

  // 네이티브 앱 뒤로가기 버튼 등록
  private async registerBackButton() {
    this.backSub = await App.addListener("backButton", () => {
      if (this.isPurchaseConfirmOpen) {
        this.modalManager.close();
        this.isPurchaseConfirmOpen = false;
      }
    });
  }
}
